import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  MenuItem,
  Select,
  LinearProgress,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import * as XLSX from "xlsx";
import GroupsIcon from "@mui/icons-material/Groups";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import toast from "react-hot-toast";
import { axiosPartners } from "../../axios/axiosConfig";

const BROKER_DASHBOARD_URL = "/brokers/dashboard";

const BROKERS = [
  {
    key: "bigul",
    name: "Bigul",
    gradient: "linear-gradient(135deg, #fde9ea 0%, #fbd4d8 100%)",
    accent: "#dc2626",
  },
  {
    key: "dhan",
    name: "Dhan",
    gradient: "linear-gradient(135deg, #e3f6f0 0%, #c9ece2 100%)",
    accent: "#0f766e",
  },
];

// Per-broker trade metric keys shown in the Broker-wise Client Report table
const TRADE_METRICS = [
  { key: "price_trade_pending_count", label: "Price Trade Pending" },
  { key: "price_trade_completed_count", label: "Price Trade Completed" },
  { key: "manual_trade_completed_count", label: "Manual Trade Completed" },
  { key: "signal_trade_completed_count", label: "Signal Trade Completed" },
  { key: "signal_trade_pending_count", label: "Signal Trade Pending" },
  { key: "signal_trade_rejected_count", label: "Signal Trade Rejected" },
  { key: "signal_trade_cancel_count", label: "Signal Trade Cancelled" },
];

const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const textMain = "#04343a";
const textMuted = "#64748b";

const StatCard = ({ icon, iconBg, label, value }) => (
  <Box
    sx={{
      flex: "1 1 220px",
      minWidth: 220,
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: "14px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      p: "18px 20px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: "10px", background: iconBg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 12.5, color: textMuted, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: 26, fontWeight: 800, color: textMain, lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Box>
);

const BrokerCard = ({ broker, connectedClients, todaysLogin }) => (
  <Box
    sx={{
      flex: "1 1 220px",
      minWidth: 220,
      background: broker.gradient,
      borderRadius: "14px",
      p: "18px 20px",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <AccountBalanceIcon sx={{ fontSize: 24, color: broker.accent }} />
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: textMain }}>
        {broker.name}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box>
        <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
          Connected Clients
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: textMain }}>
          {connectedClients.toLocaleString("en-IN")}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
          Today's Login
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: textMain }}>
          {todaysLogin.toLocaleString("en-IN")}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ChartPanel = ({ title, children }) => (
  <Box
    sx={{
      flex: "1 1 300px",
      minWidth: 300,
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: "14px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      p: "16px 18px",
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: textMain, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const BrokerConnectivity = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [brokerFilter, setBrokerFilter] = useState("all");

  const loadStats = async () => {
    setLoading(true);
    try {
      const res = await axiosPartners({ method: "get", url: BROKER_DASHBOARD_URL });
      setData(res?.data?.data?.[0] || null);
    } catch (error) {
      console.log("brokers/dashboard error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load broker connectivity stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const brokerRows = useMemo(() => {
    const rows = BROKERS.map((broker) => {
      const connected = data?.[`${broker.key}_connected_count`] ?? 0;
      const login = data?.[`${broker.key}_login_today`] ?? 0;
      const newToday = data?.[`${broker.key}_new_client_today`] ?? 0;
      const connectionRate = connected ? Math.round((login / connected) * 100) : 0;
      const trade = Object.fromEntries(
        TRADE_METRICS.map((m) => [m.key, data?.[`${broker.key}_${m.key}`] ?? 0]),
      );
      return { ...broker, connected, login, newToday, connectionRate, trade };
    });
    return brokerFilter === "all" ? rows : rows.filter((r) => r.key === brokerFilter);
  }, [data, brokerFilter]);

  const totals = useMemo(() => {
    const connected = brokerRows.reduce((s, r) => s + r.connected, 0);
    const login = brokerRows.reduce((s, r) => s + r.login, 0);
    const newToday = brokerRows.reduce((s, r) => s + r.newToday, 0);
    const connectionRate = connected ? Math.round((login / connected) * 100) : 0;
    const trade = Object.fromEntries(
      TRADE_METRICS.map((m) => [m.key, brokerRows.reduce((s, r) => s + r.trade[m.key], 0)]),
    );
    return { connected, login, newToday, connectionRate, trade };
  }, [brokerRows]);

  const pieData = brokerRows.map((r) => ({ name: r.name, value: r.connected, color: r.accent }));
  const newClientsPieData = brokerRows.map((r) => ({ name: r.name, value: r.newToday, color: r.accent }));
  const loginBarData = brokerRows.map((r) => ({ name: r.name, login: r.login, color: r.accent }));
  const comparisonData = brokerRows.map((r) => ({
    name: r.name,
    "Connected Clients": r.connected,
    "Today's Login": r.login,
  }));

  const handleExport = () => {
    const rows = brokerRows.map((r) => ({
      Broker: r.name,
      "Total Connected Clients": r.connected,
      "Today's Login Clients": r.login,
      "New Connections Today": r.newToday,
      "Connection Rate (%)": r.connectionRate,
      ...Object.fromEntries(TRADE_METRICS.map((m) => [m.label, r.trade[m.key]])),
    }));
    rows.push({
      Broker: "Total",
      "Total Connected Clients": totals.connected,
      "Today's Login Clients": totals.login,
      "New Connections Today": totals.newToday,
      "Connection Rate (%)": totals.connectionRate,
      ...Object.fromEntries(TRADE_METRICS.map((m) => [m.label, totals.trade[m.key]])),
    });
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Broker Connectivity");
    XLSX.writeFile(workbook, `broker-connectivity-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Downloaded successfully!");
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <AccountBalanceIcon sx={{ color: "#fff", fontSize: 32 }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
              Broker Connectivity Dashboard
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
              Track client connections and logins across brokers
            </Typography>
          </Box>
        </Box>

        {/* <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            size="small"
            value={brokerFilter}
            onChange={(e) => setBrokerFilter(e.target.value)}
            sx={{
              background: "#fff", borderRadius: "8px", minWidth: 140, fontSize: 13,
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            }}
          >
            <MenuItem value="all">All Brokers</MenuItem>
            {BROKERS.map((b) => (
              <MenuItem key={b.key} value={b.key}>{b.name}</MenuItem>
            ))}
          </Select>
          <Button
            onClick={loadStats}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              background: "rgba(255,255,255,0.1)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px", fontSize: 13, fontWeight: 700, textTransform: "none",
              "&:hover": { background: "rgba(255,255,255,0.18)" },
            }}
          >
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            sx={{
              background: "#016551", color: "#fff", borderRadius: "8px", fontSize: 13, fontWeight: 700,
              textTransform: "none", boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
            }}
          >
            Export
          </Button>
        </Box> */}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#016551" }} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Stat cards */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatCard
              icon={<GroupsIcon sx={{ fontSize: 18, color: "#2563eb" }} />}
              iconBg="#dbeafe"
              label="Total Connected Clients (All Brokers)"
              value={totals.connected.toLocaleString("en-IN")}
            />
            {brokerRows.map((r) => (
              <BrokerCard key={r.key} broker={r} connectedClients={r.connected} todaysLogin={r.login} />
            ))}
          </Box>

          {/* Charts */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <ChartPanel title="Broker-wise Connected Clients">
              <Box sx={{ position: "relative", height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                      {pieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString("en-IN")} />
                    <Legend verticalAlign="bottom" height={24} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute", top: "42%", left: "50%", transform: "translate(-50%, -50%)",
                    textAlign: "center", pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: textMain }}>
                    {totals.connected.toLocaleString("en-IN")}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: textMuted }}>Total Connected</Typography>
                </Box>
              </Box>
            </ChartPanel>

            <ChartPanel title="New Clients Today (Broker-wise)">
              <Box sx={{ position: "relative", height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={newClientsPieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                      {newClientsPieData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString("en-IN")} />
                    <Legend verticalAlign="bottom" height={24} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute", top: "42%", left: "50%", transform: "translate(-50%, -50%)",
                    textAlign: "center", pointerEvents: "none",
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: textMain }}>
                    {totals.newToday.toLocaleString("en-IN")}
                  </Typography>
                  <Typography sx={{ fontSize: 9.5, color: textMuted }}>New Today</Typography>
                </Box>
              </Box>
            </ChartPanel>

            <ChartPanel title="Today's Login Clients (Broker-wise)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={loginBarData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => v.toLocaleString("en-IN")} />
                  <Bar dataKey="login" radius={[6, 6, 0, 0]}>
                    {loginBarData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartPanel>

            <ChartPanel title="Connected Clients vs Today's Login">
              <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
                {brokerRows.map((r) => (
                  <Box
                    key={r.key}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.75,
                      background: "#f1f5f9", borderRadius: "8px", px: 1.25, py: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: r.accent }}>
                      {r.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: textMain }}>
                      {r.connectionRate}% conversion
                    </Typography>
                  </Box>
                ))}
              </Box>
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => v.toLocaleString("en-IN")} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Connected Clients" fill="#2563eb" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Today's Login" fill="#16a34a" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartPanel>
          </Box>

          {/* Broker-wise table */}
          <Box
            sx={{
              background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "14px",
              boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden",
            }}
          >
            <Box sx={{ p: "14px 18px", borderBottom: `1px solid ${cardBorder}` }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: textMain }}>
                Broker-wise Client Report
              </Typography>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <Box component="thead">
                  <Box component="tr">
                    {[
                      "Broker",
                      "Total Connected Clients",
                      "Today's Login Clients",
                      "New Connections Today",
                      "Connection Rate",
                      ...TRADE_METRICS.map((m) => m.label),
                    ].map((h) => (
                      <Box
                        component="th"
                        key={h}
                        sx={{ textAlign: "left", fontSize: 10.5, color: "#fff", background: "#04343a", textTransform: "uppercase", p: "10px 16px", fontWeight: 700, whiteSpace: "nowrap" }}
                      >
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {brokerRows.map((r) => (
                    <Box component="tr" key={r.key} sx={{ borderBottom: `1px solid ${cardBorder}` }}>
                      <Box component="td" sx={{ p: "10px 16px", display: "flex", alignItems: "center", gap: 1, whiteSpace: "nowrap" }}>
                        <AccountBalanceIcon sx={{ fontSize: 16, color: r.accent }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textMain }}>{r.name}</Typography>
                      </Box>
                      <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, color: textMain }}>{r.connected.toLocaleString("en-IN")}</Box>
                      <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, color: textMain }}>{r.login.toLocaleString("en-IN")}</Box>
                      <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, color: textMain }}>{r.newToday.toLocaleString("en-IN")}</Box>
                      <Box component="td" sx={{ p: "10px 16px", minWidth: 140 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(r.connectionRate, 100)}
                            sx={{ flex: 1, height: 6, borderRadius: 3, background: "#e2e8f0", "& .MuiLinearProgress-bar": { background: "#16a34a" } }}
                          />
                          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: textMain }}>{r.connectionRate}%</Typography>
                        </Box>
                      </Box>
                      {TRADE_METRICS.map((m) => (
                        <Box component="td" key={m.key} sx={{ p: "10px 16px", fontSize: 12.5, color: textMain }}>
                          {r.trade[m.key].toLocaleString("en-IN")}
                        </Box>
                      ))}
                    </Box>
                  ))}
                  <Box component="tr">
                    <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain, whiteSpace: "nowrap" }}>Total</Box>
                    <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain }}>{totals.connected.toLocaleString("en-IN")}</Box>
                    <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain }}>{totals.login.toLocaleString("en-IN")}</Box>
                    <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain }}>{totals.newToday.toLocaleString("en-IN")}</Box>
                    <Box component="td" sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain }}>{totals.connectionRate}%</Box>
                    {TRADE_METRICS.map((m) => (
                      <Box component="td" key={m.key} sx={{ p: "10px 16px", fontSize: 12.5, fontWeight: 800, color: textMain }}>
                        {totals.trade[m.key].toLocaleString("en-IN")}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BrokerConnectivity;
